import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ToastrService} from 'ngx-toastr';
import {ProductionJob} from '../../production-job';
import {PotProductionJobService} from '../../potting-production-job.service';
import {CreateStockWithProductionJob} from './create-stock-with-production-job';

@Component({
  selector: 'app-printing-dialog',
  templateUrl: './create-stock-dialog.component.html',
  styleUrls: ['./create-stock-dialog.component.scss']
})
export class CreateStockDialogComponent {

  originalAmount: number;
  inputMaterialAmount: number;
  inputMaterialAmountTrashed: number;
  remarks: string;
  location: string;
  productionDate: Date;
  bookAutomaticallyFromStock: boolean;
  productionJob: ProductionJob;
  allowMarkingAsProduced: boolean;
  isLoading: boolean;
  plantName: string;
  materialUnitSizeId: number;
  targetUnitSizeId: number;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<CreateStockDialogComponent>,
    private potProductionJobService: PotProductionJobService,
    private toastrService: ToastrService
  ) {
    this.plantName = data.plantName;
    this.productionJob = data.productionJob as ProductionJob;
    this.allowMarkingAsProduced = data.allowMarkingAsProduced;

    const reserved = this.productionJob.totalReservedAmount ?? 0;

    this.originalAmount = this.productionJob.orderedAmount ?? 0;
    this.remarks = this.productionJob.remarks;
    this.bookAutomaticallyFromStock = this.productionJob.hasReservedStock;
    this.productionDate = new Date();
    this.isLoading = false;
    this.materialUnitSizeId = this.productionJob.materialUnitSizeId;
    this.targetUnitSizeId = this.productionJob.targetUnitSizeId;

    if (this.productionJob.hasReservedStock) {
      this.inputMaterialAmount = reserved;
      this.inputMaterialAmountTrashed = 0;
    } else {
      this.inputMaterialAmount = this.originalAmount;
      this.inputMaterialAmountTrashed = 0;
    }

    this.location = '';
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private normalizeToZero(value: any): number {
    const num = Number(value);
    if (isNaN(num) || value === null || value === undefined || value === '') {
      return 0;
    }
    return num;
  }

  async onSubmitClickCreateStock() {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    const original = this.normalizeToZero(this.originalAmount);
    const used = this.normalizeToZero(this.inputMaterialAmount);
    const trashed = this.normalizeToZero(this.inputMaterialAmountTrashed);

    const newStockEntity = new CreateStockWithProductionJob(
      original,
      this.location,
      this.remarks,
      this.productionJob.id,
      this.productionDate,
      used,
      trashed,
      this.bookAutomaticallyFromStock,
      this.targetUnitSizeId,
      this.materialUnitSizeId
    );

    try {
      await this.potProductionJobService.createNewStockEntity(newStockEntity)
        .toPromise()
        .then(() => {
          this.toastrService.success('Nieuwe voorraad succesvol aangemaakt!');
        })
        .catch(err => {
          this.toastrService.error('Er is iets misgegaan');
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
          this.dialogRef.close();
        });
    } catch (e) {
      this.toastrService.error('Er is iets mis gegaan!');
      console.error(e);
      this.isLoading = false;
    }
  }

  isDisabled() {
    const original = this.normalizeToZero(this.originalAmount);
    if (original <= 0 || !this.location || this.location.trim().length === 0) {
      return true;
    }

    if (this.productionJob.hasReservedStock) {
      const reserved = this.productionJob.totalReservedAmount ?? 0;
      const used = this.normalizeToZero(this.inputMaterialAmount);
      const trashed = this.normalizeToZero(this.inputMaterialAmountTrashed);

      if (used + trashed > reserved) {
        return true;
      }
    }

    return false;
  }

  async onSubmitClickMarkAsProduced() {
    this.isLoading = true;
    await this.potProductionJobService.markProductionJobAsProduced(this.productionJob.id)
      .toPromise()
      .then(() => this.toastrService.success('Productie opdracht als geproduceerd gemarkeerd!'))
      .catch(err => {
        console.error(err);
        this.toastrService.error('Er is iets misgegaan');
      })
      .finally(() => {
        this.isLoading = false;
        this.dialogRef.close();
      });
  }

  onInputAmountUsedChanged(value: number | null) {
    let used = this.normalizeToZero(value);

    if (this.productionJob.hasReservedStock) {
      const reserved = this.productionJob.totalReservedAmount ?? 0;

      if (used < 0) {
        used = 0;
      }
      if (used > reserved) {
        used = reserved;
      }

      this.inputMaterialAmount = used;
      this.inputMaterialAmountTrashed = reserved - used;
    } else {
      if (used < 0) {
        used = 0;
      }
      this.inputMaterialAmount = used;
    }
  }

  onTrashedInput(event: Event) {
    const reserved = this.productionJob.totalReservedAmount ?? 0;
    const inputEl = event.target as HTMLInputElement;

    let trashed = this.normalizeToZero(inputEl.value);

    if (this.productionJob.hasReservedStock) {
      if (trashed < 0) {
        trashed = 0;
      }

      const used = this.normalizeToZero(this.inputMaterialAmount);
      const maxTrashedGivenUsed = Math.max(0, reserved - used);

      if (trashed > maxTrashedGivenUsed) {
        trashed = maxTrashedGivenUsed;
      }
    } else {
      if (trashed < 0) {
        trashed = 0;
      }
    }

    this.inputMaterialAmountTrashed = trashed;

    inputEl.value = String(trashed);
  }

  onOriginalAmountChanged(value: number | null) {
    this.originalAmount = this.normalizeToZero(value);
  }
}
